import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {PaneBackButtonComponent} from "../pane-back-button/pane-back-button.component";
import * as ChatSelectors from "../../state/chats/chat.selector";
import { Store } from "@ngrx/store";
import {Chat, Message, Participant} from "../../state/chats/chat.model";
import {ActivatedRoute, Route} from "@angular/router";
import {FormsModule} from "@angular/forms";
import {FaIconComponent, FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import {ProfileImageComponent} from "../profile-image/profile-image.component";
import * as AuthSelectors from "../../state/auth/auth.selector";
import * as UserSelectors from "../../state/users/users.selector";
import {User} from "../../auth/primitives/user";
import {FilesService} from "../../services/files/files.service";
import {Base64ImageComponent} from "../base64-image/base64-image.component";
import {NgForOf, NgIf} from "@angular/common";
import {ChatsService} from "../../services/chats.service";
import {ChatMessageComponent} from "../chat-message/chat-message.component";
import {ChatActions} from "../../state/chats/chatActions";
import {updateLastSeen} from "../../state/chats/chat.effects";
import {Base} from "../base-component/base-component";
import {IconsService} from "../../services/icons.service";

@Component({
  selector: 'chat',
  standalone: true,
  imports: [
    PaneBackButtonComponent,
    FormsModule,
    Base64ImageComponent,
    NgForOf,
    NgIf,
    ChatMessageComponent,
    FaIconComponent
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent extends Base implements OnInit {
  chat: Chat
  user: User
  otherGuy: User
  participants: Participant[] = []
  newMessageText: string = ""

  users: User[]
  usersById: {[key: string]: User} = {}
  profileImages: {[key: string]: string} = {}

  chatImageBase64: string

  messages: Message[] = []


  @ViewChild('messageContainer') messageContainer!: ElementRef<HTMLDivElement>;

  private readonly BOTTOM_THRESHOLD = 40; // px

  constructor(
      private store: Store,
      private route: ActivatedRoute,
      private fileService: FilesService,
      private chatService: ChatsService,
      protected iconsService: IconsService
  ) {
    super()
  }

  ngOnInit(): void {
    let querySub = this.route.queryParams.subscribe(params => {
      const chatId = params['chatId'];
      this.subs.add(this.store.select(ChatSelectors.selectChatById(chatId)).subscribe((chat) => {
        this.chat = chat
      }))
      this.subs.add(this.store.select(AuthSelectors.selectUser).subscribe(async (user) => {
        this.user = user
        await this.gotParticipantsAndUser()
      }))
      this.subs.add(this.store.select(ChatSelectors.selectParticipantsForChat(chatId)).subscribe(async (participants) => {
        this.participants = participants
        await this.gotParticipantsAndUser()
      }))
      this.subs.add(this.store.select(ChatSelectors.selectMessagesForChat(chatId)).subscribe((messages) => {
        this.updateLastSeen()
        const stickToBottom = this.isAtBottom();
        if (this.messages.length === 0)  {
          this.messages = this.injectDisplayMetadata(messages)
          this.scrollToBottom()
          return
        }
        const keys = this.messages.map(message => message.id)
        const missingMessages =
            this.injectDisplayMetadata(
                messages.filter(m => !keys.includes(m.id))
            )
        this.messages.push(...missingMessages)
        if (stickToBottom) {
          this.scrollToBottom()
        }
      }))
    });
    this.subs.add(querySub)
  }

  updateLastSeen() {
    if (!this.user) return
    if (!this.chat) return
    this.store.dispatch(ChatActions.updateLastSeen({
      userId: this.user.id,
      chatId: this.chat.id
    }))
  }

  async gotParticipantsAndUser() {
    if ((!this.user) || this.participants.length === 0) {
      return
    }
    let participantIds = this.participants.map(part => part.participant_id)
    this.subs.add(this.store.select(UserSelectors.selectUsersByIds(participantIds)).subscribe((users) => {
      this.users = users
      this.users.forEach(user => this.usersById[user.id] = user)
      if (users.length == 2) {
        this.otherGuy = users.find(user => user.id != this.user.id)
      }
      this.users.forEach(async user => {
        const serverFile = await this.fileService.getFile(user.profile_image_file_id)
        this.profileImages[user.id] = serverFile.fileData
        if (user.id != this.user.id) {
          this.chatImageBase64 = serverFile.fileData
        }
      })
    }))
  }

  autoResize(event: Event): void {
    // const textarea = event.target as HTMLTextAreaElement;
    // textarea.style.height = 'auto';  // Reset first
    // textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  }


  trackByMessageId(index: number, message: Message) {
    return message.id;
  }


  displayMetadataDay = 0
  displayMetadataMonth = 0
  displayMetadataYear = 0
  displayMetadataLastAuthor = ""

  injectDisplayMetadata(messages: Message[]): Message[] {
    const outputMessages = []
    for (const message of messages) {
      let isFirstOfDay = false
      let sameAuthor = true
      const mDay = new Date(message.created_at).getDate()
      const mMonth = new Date(message.created_at).getMonth()
      const mYear = new Date(message.created_at).getFullYear()
      if (
          this.displayMetadataDay != mDay ||
          this.displayMetadataMonth != mMonth ||
          this.displayMetadataYear != mYear
      ) {
        isFirstOfDay = true
        this.displayMetadataDay = mDay;
        this.displayMetadataMonth = mMonth;
        this.displayMetadataYear = mYear;
      }
      if (message.owner_id != this.displayMetadataLastAuthor) {
        sameAuthor = false
        this.displayMetadataLastAuthor = message.owner_id
      }
      outputMessages.push({...message, isFirstOfDay, sameAuthor})
    }
    return outputMessages
  }


  private isAtBottom(): boolean {
    if (!this.messageContainer) {
      return false
    }
    const el = this.messageContainer.nativeElement;

    return (
        el.scrollHeight - el.scrollTop - el.clientHeight
        < this.BOTTOM_THRESHOLD
    );
  }

  scrollToBottom(): void {
    try {
      requestAnimationFrame(() => {
        const el = this.messageContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      })
    } catch (e) {
      console.log(e)
    }
  }


  async sendMessage() {
    if (!this.newMessageText) {
      return
    }
    await this.chatService.newMessage(
        this.chat.id,
        this.newMessageText,
        this.user.id,
        [], {}
    )
    this.newMessageText = ""
  }

}
